//@ts-check
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useAppDispatch } from "src/utils/ReduxHooks";
import * as Yup from 'yup';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { endPoint } from "src/AppConstants";
import FormTextInput from "./FormTextInput";
import { v4 as uuid } from 'uuid'
import useTypedSelector from "utils/useTypedSelector";
import LnIcon from "components/LnIcon";
import { userDuck } from "./UserDuck";
import FileUpload from "components/FileUpload/FileUpload";
import { Confirm, Input, ModalConstants } from "components/OnlineModal";
import { fileUploadDuck } from "components/FileUpload/FileUploadDuck"
import classes from "./user.module.scss";
import UserImg from "components/UserImg";
import { UncontrolledAlert } from "reactstrap";
import ChangePasswordModal from "./ChangePasswordModal";



/**
 * 
 * @param {{onCancelForm: ()=>void, userData: import('./User').UserDetailDto , t: import('features/language/languageService').LanguageService}} props 
 */
const UserDataEditForm = ({ userData, t, onCancelForm }) => {

    /** @type {import('redux').Dispatch<any>} */
    const dispatch = useAppDispatch();
    const user = useTypedSelector(state => state.login.user);
    const userImage = endPoint.GET_USER_IMAGE_URL(userData.Id) + `?x=${uuid()}`;
    const imagePreviews = useTypedSelector(state => state.fileUpload.imagePreviews);

    const [submitting, setSubmitting] = useState(false);
    const [showIsSavedAlert, setShowIsSavedAlert] = useState(false);
    const [showIsSavedAlertTimer, setShowIsSavedAlertTimer] = useState(-1);
    const [displayPDExplain, setDisplayPDExplain] = useState(false);
    const [displayAdaptionsExplain, setDisplayAdaptionsExplain] = useState(false);
    const [uniqueAddress, setUniqueAddress] = useState({});
    const [formReady, setFormReady] = useState(false);

    const uploadActionRef = useRef(null);

    const formSchema = {
        Alias: Yup.string()
            .test('isUniqueAlias', t.getText('nickname.exists'),
                async function (value) {
                    return await handleIsUnique('Alias', 'alias', value);
                }),
        em: Yup.string()
            .email(t.getText('bad_email_adress'))
            .required(t.getText('required_field'))
            .test('isUniqueEmail', t.getText('user.might.already.exist.description'),
                async function (value) {
                    return await handleIsUnique('Email', 'mail', value);
                }),
        em2: Yup.string()
            .email(t.getText('bad_email_adress'))
            .test('isUniqueEmail2', t.getText('user.might.already.exist.description'),
                async function (value) {
                    return await handleIsUnique('Email2', 'mail', value);
                }),
        fn: Yup.string()
            .required(t.getText('required_field')),
        ln: Yup.string()
            .required(t.getText('required_field')),
        ShowPersonalData: Yup.boolean(),
        Adaptions: Yup.boolean(),
    };



    const checks = Yup.object(formSchema);

    const { register, handleSubmit, formState, formState: { errors }, reset } = useForm({
        resolver: yupResolver(checks),
        mode: "onBlur"
    });

    const { isDirty, isValid } = useMemo(() => formState, [formState]);


    const resetForm = () => {
        reset();
        onCancelForm();
    }

    useEffect(() => {
        setFormReady(false);
        reset({
            ShowPersonalData: userData.Info.ShowPersonalData,
            Adaptions: userData.Teacher ? userData.Teacher.Adaptions : null
        });
        window.setTimeout(() => {
            setFormReady(true);
        }, 500);
    }, [userData.Teacher, reset, userData.Info.ShowPersonalData]);

    useEffect(() => {
        return () => {
            if (showIsSavedAlertTimer > -1) {
                window.clearTimeout(showIsSavedAlertTimer);
            }
        }
    }, [showIsSavedAlertTimer])



    /**
    *
    * @param {number} type
    */
    const sendEmailVerification = (type) => {
        dispatch(userDuck.sendVerifyEmail(userData.Id, type));
    }

    const nameDict = useMemo(() => {
        return {
            FirstName: "fn",
            LastName: "ln",
            Email: "em",
            Email2: "em2",
            street: "st",
            zipcode: "zc",
            city: "cy",
            tele: "t1",
            tele2: "t2",
            invoiceorg: "invo",
            invoiceorg2: "invo2",
            invoicestreet: "invstr",
            invoicezip: "invz",
            invoicecity: "invci",
            invoicecountry: "invco"
        }
    }, []);





    /**
     *
     * @param {1|2} type
     */
    const verifyEmail = async (type) => {

        const result = await Confirm({
            buttons: [],
            backdrop: 'static',
            class: null,
            title: t.getText("verify"),
            message: (<div className="preserve-white">{t.getText("really.verify.email")}</div>),
            languageService: t,
            type: "yes/no"
        });

        if (result === ModalConstants.Yes) {
            dispatch(userDuck.verifyEmail(userData.Id, type));
        };



    }


    let openPasswdFunction = () => { };
    const fileOptions = { AcceptFileTypes: "bmp, gif, png, tif, tiff, jpg, jpeg, jfif, jfi, webp", MaxFileSize: 5242880 };
    /**
     *
     * @param {{key:string, name:string, size:number, type:string}} file
     */
    const handleFileUploadReady = async (file) => {

        const doUpload = async (passwd) => {
            const data = {
                FileName: file.name,
                FileTempName: file.key,
                Password: passwd
            }
            try {
                await dispatch(userDuck.uploadAvatar(userData.Id, data));
            }
            finally {
                dispatch(fileUploadDuck.setImagePreviews(null));
                uploadActionRef.current = null;
            }
        }

        if (user.Id !== userData.Id && userData.CanEdit) {
            doUpload(null);
        }
        else {
            const promise = Input({
                message: null,
                closeOnEnter: true,
                title: t.getText("personal.info.password"),
                languageService: t,
                fields: [{ name: "password", type: "password", label: t.getText("passwd"), placeholder: "" }]
            }).promise;

            promise.then(async (dialogresult) => {
                if (!dialogresult || !dialogresult.password) {
                    return;
                }

                doUpload(dialogresult.password);

            });
        }
    }

    const imagePreviewReady = (action) => {
        uploadActionRef.current = action;
    }

    const doSaveImage = () => {
        uploadActionRef.current();
    }

    const deleteUserImage = () => {

        const doDeleteImage = async (passwd) => {

            try {
                await dispatch(userDuck.deleteAvatar(userData.Id, passwd));
            }
            finally {
                dispatch(fileUploadDuck.setImagePreviews(null));
                uploadActionRef.current = null;
            }
        }

        if (user.Id !== userData.Id && userData.CanEdit) {
            doDeleteImage(null);
        }
        else {
            const promise = Input({
                message: <div><div className="preserve-white">{t.getText("delete_general_confirm")}</div>
                    <div className="mt-3">{t.getText("personal.info.password")}</div></div>,
                closeOnEnter: true,
                title: t.getText("image.delete"),
                languageService: t,
                fields: [{ name: "password", type: "password", label: t.getText("passwd"), placeholder: "" }]
            }).promise;

            promise.then(async (dialogresult) => {
                if (!dialogresult || !dialogresult.password) {
                    return;
                }

                doDeleteImage(dialogresult.password);

            });
        }
    }

    const handleImageCancel = () => {
        dispatch(fileUploadDuck.setImagePreviews(null));
        uploadActionRef.current = null;
    }


    const handleIsUnique = useMemo(() => async (target, propname, value) => {
        if (formReady === false) {
            return true;
        }
        else if (value === "") {
            return true;
        } else {
            const address = value.toLowerCase();
            if (uniqueAddress[address] === propname) {
                return true;
            }
            const isUnique = await userDuck.isUnique(propname, address, userData.Id);

            const res = isUnique === 'success';
            if (res) {
                let newdata = { ...uniqueAddress };
                newdata[address] = propname;
                setUniqueAddress(newdata);
            }
            return res;
        }
    }, [formReady, userData.Id, uniqueAddress]);

    const handleFormSubmit = async (values) => {

        if (userData.Id === user.Id) {
            Input({
                message: null,
                title: t.getText("personal.info.password"),
                languageService: t,
                fields: [{ name: "password", type: "password", label: t.getText("passwd"), placeholder: "" }],
                closeOnEnter: true
            }).promise
                .then((dialogresult) => {
                    if (!dialogresult || !dialogresult.password) {
                        setSubmitting(false);
                        return;
                    }

                    doSubmit(dialogresult.password, values);
                });
        }
        else {
            doSubmit(null, values);
        }

    }

    const doSubmit = async (passwd, values) => {


        setSubmitting(true);

        const postData = getpostData(values, passwd);

        try {
            await dispatch(userDuck.saveUserData(userData.Id, postData));
            setSubmitting(false);
            setFormReady(false);
            window.setTimeout(() => {
                reset(values);
                setFormReady(true);
            }, 10);

            setShowIsSavedAlert(true);

            const id = window.setTimeout(() => {
                setShowIsSavedAlertTimer(-1);
                setShowIsSavedAlert(false);
            }, 5000);

            setShowIsSavedAlertTimer(id);


        } catch (error) {
            console.error(error);
            setSubmitting(false);
        }
    };


    const getpostData = (values, passwd) => {
        const postData = {
            Alias: values.Alias,
            Password: passwd,
            FirstName: null,
            LastName: null,
            ShowPersonalData: values.ShowPersonalData,
            Mail2: null,
            Mail: null,
            Properties: [],
        }

        if (userData.Teacher) {
            postData.Adaptions = values.Adaptions;
        }

        if (!userData.Info.Email.WriteOnce) {
            postData.Mail = values[nameDict["Email"]];
        }

        if (!userData.Info.Email2.WriteOnce) {
            postData.Mail2 = values[nameDict["Email2"]];
        }

        if (!userData.Info.FirstNameWriteOnce) {
            postData.FirstName = values[nameDict["FirstName"]];
        }
        if (!userData.Info.LastNameWriteOnce) {
            postData.LastName = values[nameDict["LastName"]];
        }

        const properties = [];

        userData.Info.Properties.forEach(p => {

            if (!p.WriteOnce) {
                let val = values[infoPropertyNames[p.Key]];
                if (val === "") {
                    val = null;
                }
                properties.push({
                    Value: val,
                    Key: p.Key,
                })
            }
        });
        postData.Properties = properties;

        return postData;
    }

    const togglePDExplain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDisplayPDExplain(!displayPDExplain);
    }

    const toggleAdaptionsExplain = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDisplayAdaptionsExplain(!displayAdaptionsExplain);
    }


    const infoPropertyNames = useMemo(() => {

        const obscureKey = (key) => {
            return nameDict[key] || key;
        }

        const names = {};
        userData.Info.Properties.forEach(p => {
            const safeKey = p.Key.replace(/\./g, '_');
            names[p.Key] = obscureKey(safeKey);
            if (p.Required) {
                formSchema[safeKey] = Yup.string()
                    .required(t.getText('required_field'));

            }
        });

        return names;

    }, [userData.Info.Properties, formSchema, t, nameDict]);





    const openPasswd = () => {
        openPasswdFunction();
    }


    return (
        <>
            <ChangePasswordModal currentUser={user} userData={userData} getOpenFunction={(f) => {
                openPasswdFunction = f;
            }
            } />
            <form onLoad={() => setFormReady(true)} className="mb-5" onSubmit={handleSubmit(handleFormSubmit)}>


                <div className={["row", classes.formRow].join(' ')}>
                    <div className="col-sm-3 col-md-2">

                        <FileUpload previewReady={imagePreviewReady}
                            className="avatar-upload" hideDox={true} imagePreviewOptions={[{ name: "small", maxheight: 200, maxwidth: 200 }]}
                            handleUploadReady={handleFileUploadReady} accept={fileOptions.AcceptFileTypes} languageService={t}
                            maxsize={fileOptions.MaxFileSize} >
                            {imagePreviews ? <div>
                                <img src={imagePreviews.small.src} alt={"preview"} />
                                <br />
                                <button type="button" onClick={doSaveImage} className="btn btn-small btn-primary mb-3 mt-3 mr-4" >{t.getText("save.short")}</button>
                                <button type="button" onClick={handleImageCancel} className="btn btn-small btn-inverse" >{t.getText("cancel")}</button>

                            </div> :
                                <UserImg src={userImage} className="" alt={t.getText('user')} />
                            }
                            <div>
                                <button type="button" className="mt-3 btn btn-danger btn-inverse btn-small" onClick={deleteUserImage} >{t.getText("image.delete")}</button>
                            </div>

                        </FileUpload>

                    </div>
                    <div className="col-sm-9 col-md-10">

                        {userData.CanEdit && <>
                            <div className="row">
                                <div className="col">
                                    <div className="custom-control custom-checkbox">

                                        <input {...register("ShowPersonalData")}
                                            type="checkbox"
                                            name="ShowPersonalData"
                                            className="custom-control-input"
                                            id="id_ShowPersonalData"

                                        />
                                        <label
                                            className="custom-control-label pt-1"
                                            htmlFor="id_ShowPersonalData" >
                                            {t.getText('show.personal.data')}
                                            <button type="button" className="btn btn-link ml-4" onClick={(e) => togglePDExplain(e)}>
                                                <LnIcon className="icon-small" name="questionmark-icon" />
                                            </button>
                                        </label>
                                    </div>
                                    <br />
                                    {displayPDExplain && <div className="alert alert-warning preserve-white"> <small>
                                        {t.getText('show.personal.data.explain')}
                                    </small></div>}
                                </div>

                                {userData.Teacher &&
                                    <div className="col">

                                        <div className="custom-control custom-checkbox">

                                            <input {...register("Adaptions")}
                                                type="checkbox"
                                                name="Adaptions"
                                                className="custom-control-input"
                                                id="id_Adaptions"

                                            />
                                            <label
                                                className="custom-control-label pt-1" htmlFor="id_Adaptions" >

                                                {t.getText('adaptions')}
                                                <LnIcon name="lifebuoy2" className="ml-3" />
                                                <button type="button" className="btn btn-link ml-4" onClick={(e) => toggleAdaptionsExplain(e)}>
                                                    <LnIcon className="icon-small" name="questionmark-icon" />
                                                </button>
                                            </label>
                                        </div>
                                        <br />
                                        {displayAdaptionsExplain && <div className="alert alert-warning preserve-white">
                                            <small>
                                                {t.getText('adaptions.explain')}
                                            </small></div>
                                        }
                                    </div>
                                }



                            </div>

                            <UncontrolledAlert color="warning preserve-white" >
                                <small>{t.getText('userdata.explain')}</small>
                            </UncontrolledAlert>

                            <div className="row mb-4">
                                <div className="col mt-3">
                                    <button className="btn btn-primary btn-small mr-4"
                                        disabled={submitting || !isDirty || !isValid}
                                        type="submit" ><LnIcon className="icon-small filter-white mr-4" name="edit-icon" /> {t.getText('save.short')}</button>
                                    <button className="btn btn-inverse btn-small" type="button" onClick={() => resetForm()}><LnIcon className="icon-small mr-4" name="cross-icon" /> {t.getText('cancel.edit')} </button>
                                </div>
                            </div>

                        </>
                        }

                        <div className="row">
                            <div className="col-lg-4 col-md-6 mb-3">
                                <FormTextInput
                                    as="input"
                                    errors={errors}
                                    registerFunc={register}
                                    label={t.getText('firstname')}
                                    name={nameDict["FirstName"]}
                                    type="text"
                                    readOnly={userData.Info.FirstNameWriteOnce}
                                    defaultValue={userData.Info.FirstName || ''}
                                />
                            </div>
                            <div className="col-lg-4 col-md-6 mb-3">
                                <FormTextInput
                                    as="input"
                                    errors={errors}
                                    registerFunc={register}
                                    label={t.getText('lastname')}
                                    name={nameDict["LastName"]}
                                    type="text"
                                    readOnly={userData.Info.LastNameWriteOnce}
                                    defaultValue={userData.Info.LastName || ''}
                                />
                            </div>
                            {(userData.Info.Email.Address && userData.Info.Email.Address.length > 0) &&
                                <div className="col-lg-4 col-md-6 mb-3">
                                    <FormTextInput
                                        as="input"
                                        errors={errors}
                                        registerFunc={register}
                                        label={<div className="d-flex justify-content-between">{t.getText('mail')} {!userData.Info.Email.Verified && <span className="ml-3 text-danger"> {t.getText("unverified")} <LnIcon name="alert" className="icon-small" /></span>}</div>}
                                        name={nameDict["Email"]}
                                        type="email"
                                        readOnly={userData.Info.Email.WriteOnce}
                                        defaultValue={userData.Info.Email.Address || ''}
                                    />
                                    {!userData.Info.Email.Verified && <>
                                        <button type="button" onClick={() => sendEmailVerification(1)} className="btn btn-inverse btn-small mr-3 mb-3">{t.getText("send.verification.email")}</button>
                                        {(user.Id !== userData.Id) && <button type="button" onClick={() => verifyEmail(1)} className="btn btn-inverse btn-small">{t.getText("verify")}</button>}
                                    </>}

                                </div>
                            }
                        </div>

                        <div className="row mb-5">

                            <div className="col-lg-4 col-md-6 mb-3">
                                <FormTextInput
                                    as="input"
                                    errors={errors}
                                    registerFunc={register}
                                    label={<div className="d-flex justify-content-between">{t.getText('mail2')} {!userData.Info.Email2.Verified && <span className="ml-3 text-danger"> {t.getText("unverified")} <LnIcon name="alert" className="icon-small" /></span>}</div>}
                                    name={nameDict["Email2"]}
                                    type="email"
                                    readOnly={userData.Info.Email2.WriteOnce}
                                    defaultValue={userData.Info.Email2.Address || ''}
                                />
                                {!userData.Info.Email2.Verified && <>
                                    <button type="button" onClick={() => sendEmailVerification(2)} className="btn btn-inverse btn-small mr-3 mb-3">{t.getText("send.verification.email")}</button>
                                    {(user.Id !== userData.Id) && <button type="button" onClick={() => verifyEmail(2)} className="btn btn-inverse btn-small">{t.getText("verify")}</button>}
                                </>}
                            </div>

                            <div className="col-lg-4 col-md-6 mb-3">
                                <FormTextInput
                                    as="input"
                                    errors={errors}
                                    registerFunc={register}
                                    label={t.getText('alias')}
                                    name="Alias"
                                    type="text"
                                    readOnly={false}
                                    defaultValue={userData.Info.Alias || ''}
                                />
                            </div>

                            <div className="col-lg-4 col-md-6 mb-3">
                                <label className="d-block">{t.getText('passwd')}</label>
                                <button type="button" className="btn btn-inverse btn-small mr-4" onClick={openPasswd}>{t.getText('password.replace')}</button>
                            </div>


                        </div>

                        <div className="row mb-4">

                            {userData.Info.Properties.map(p => {
                                return (
                                    <div key={p.Key} className="col-lg-4 col-md-6 mb-3">
                                        <FormTextInput
                                            label={t.getText(p.Key)}
                                            errors={errors}
                                            registerFunc={register}
                                            name={infoPropertyNames[p.Key]}
                                            as={p.Multiline ? "textarea" : "input"}
                                            type={p.InputType || 'text'}
                                            readOnly={p.WriteOnce}
                                            defaultValue={p.Value || ''}
                                        />

                                    </div>
                                )
                            })}

                        </div>
                        <div className="row mb-5">

                            {showIsSavedAlert && (<div className="col-sm-12">
                                <div className="alert alert-primary">
                                    {t.getText("saved")}
                                </div>
                            </div>)}

                            <div className="col-sm-12" >

                                <button className="btn btn-primary btn-small mr-4"
                                    disabled={submitting || !isDirty || !isValid}
                                    type="submit" ><LnIcon className="icon-small filter-white mr-4" name="edit-icon" /> {t.getText('save.short')}</button>
                                <button className="btn btn-inverse btn-small" type="button" onClick={() => resetForm()}><LnIcon className="icon-small mr-4" name="cross-icon" /> {t.getText('cancel.edit')} </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </>

    )

}


export default UserDataEditForm;

